// Refcounted SignalR subscriptions: one HubConnection per clientPath,
// one `on` per (clientPath, eventName), fanned out to consumer handlers.
import {
    HubConnection,
    HubConnectionBuilder,
    IRetryPolicy,
    LogLevel,
    RetryContext,
} from "@microsoft/signalr";
import { useEffect, useRef } from "react";
import { ENVIRONMENTS } from "../../constants";
import { getAccessToken } from "../../services/auth";

// --- Config ---------------------------------------------------------------

const SHORT_RETRY_ATTEMPTS = 5; // After this many failed start attempts, back off from short to long delay
const START_RETRY_DELAY_MS = 5000;
const LONG_RETRY_DELAY_MS = 60000;

const RECONNECT_DELAYS_MS = [0, 2000, 5000, 10000];
const RECONNECT_MAX_DELAY_MS = 30000;

const HUB_LOG_LEVEL =
    process.env.NEXT_PUBLIC_APP_ENV === ENVIRONMENTS.DEVELOPMENT
        ? LogLevel.Information
        : LogLevel.Warning;

// Default SignalR policy gives up after ~30s; we retry forever.
const RECONNECT_POLICY: IRetryPolicy = {
    nextRetryDelayInMilliseconds: (retryContext: RetryContext) =>
        RECONNECT_DELAYS_MS[retryContext.previousRetryCount] ??
        RECONNECT_MAX_DELAY_MS,
};

// --- Connection registry --------------------------------------------------

interface HubEntry {
    connection: HubConnection;
    listenerCount: number;
    startPromise: Promise<void>;
    aborted: boolean;
    abortSleep?: () => void;
}

const connectionRegistry = new Map<string, HubEntry>();

const buildHubConnection = (url: string) =>
    new HubConnectionBuilder()
        .withUrl(url, {
            accessTokenFactory: async () => {
                const token = await getAccessToken();
                if (!token) throw new Error("No access token available");
                return token;
            },
        })
        .withAutomaticReconnect(RECONNECT_POLICY)
        .configureLogging(HUB_LOG_LEVEL)
        .build();

const startConnectionWithRetry = async (entry: HubEntry): Promise<void> => {
    for (let attempt = 1; !entry.aborted; attempt++) {
        try {
            await entry.connection.start();
            if (entry.aborted) await entry.connection.stop();
            return;
        } catch {
            const ms =
                attempt < SHORT_RETRY_ATTEMPTS
                    ? START_RETRY_DELAY_MS
                    : LONG_RETRY_DELAY_MS;
            await new Promise<void>((resolve) => {
                const timer = setTimeout(resolve, ms);
                entry.abortSleep = () => {
                    clearTimeout(timer);
                    resolve();
                };
            });
            entry.abortSleep = undefined;
        }
    }
};

const acquireHubConnection = (clientPath: string): HubEntry => {
    let entry = connectionRegistry.get(clientPath);
    if (!entry) {
        const connection = buildHubConnection(clientPath);
        const newEntry: HubEntry = {
            connection,
            listenerCount: 0,
            startPromise: Promise.resolve(),
            aborted: false,
        };
        // .catch so a rejection never becomes an unhandledRejection.
        newEntry.startPromise = startConnectionWithRetry(newEntry).catch(
            () => undefined
        );
        connectionRegistry.set(clientPath, newEntry);
        entry = newEntry;
    }
    entry.listenerCount += 1;
    return entry;
};

const releaseHubConnection = (clientPath: string): void => {
    const entry = connectionRegistry.get(clientPath);
    if (!entry) return;
    entry.listenerCount -= 1;
    if (entry.listenerCount <= 0) {
        entry.aborted = true;
        entry.abortSleep?.();
        connectionRegistry.delete(clientPath);
        // Wait for in-flight start before stop to avoid a stop-before-start race.
        void entry.startPromise
            .catch(() => undefined)
            .then(() => entry.connection.stop());
    }
};

// --- Event registry -------------------------------------------------------

interface EventEntry {
    handlers: Set<(payload: unknown) => void>;
    signalRHandler: (payload: unknown) => void;
    refCount: number;
}

const eventRegistry = new Map<string, EventEntry>();

const eventKey = (clientPath: string, eventName: string) =>
    `${clientPath}::${eventName}`;

const acquireEventEntry = (
    connection: HubConnection,
    clientPath: string,
    eventName: string
): EventEntry => {
    const key = eventKey(clientPath, eventName);
    let entry = eventRegistry.get(key);
    if (!entry) {
        const handlers = new Set<(payload: unknown) => void>();
        const signalRHandler = (payload: unknown) => {
            handlers.forEach((handler) => handler(payload));
        };
        entry = { handlers, signalRHandler, refCount: 0 };
        eventRegistry.set(key, entry);
        connection.on(eventName, signalRHandler);
    }
    entry.refCount += 1;
    return entry;
};

const releaseEventEntry = (
    connection: HubConnection,
    clientPath: string,
    eventName: string
): void => {
    const key = eventKey(clientPath, eventName);
    const entry = eventRegistry.get(key);
    if (!entry) return;
    entry.refCount -= 1;
    if (entry.refCount <= 0) {
        connection.off(eventName, entry.signalRHandler);
        eventRegistry.delete(key);
    }
};

// --- Hook -----------------------------------------------------------------

export const useHubEvent = <T>(
    clientPath: string,
    eventName: string,
    onMessage: (payload: T) => void
): void => {
    // Ref so inline callbacks with changing identity do not retear the effect.
    const onMessageRef = useRef(onMessage);
    onMessageRef.current = onMessage;

    useEffect(() => {
        const hubEntry = acquireHubConnection(clientPath);
        const eventEntry = acquireEventEntry(
            hubEntry.connection,
            clientPath,
            eventName
        );
        const handler = (payload: unknown) => {
            onMessageRef.current(payload as T);
        };
        eventEntry.handlers.add(handler);
        return () => {
            eventEntry.handlers.delete(handler);
            releaseEventEntry(hubEntry.connection, clientPath, eventName);
            releaseHubConnection(clientPath);
        };
    }, [clientPath, eventName]);
};
